// Project Unveiled — unified GA4 / Meta Pixel / TikTok Pixel tracking.
// Build marker: 2026-06-22b — force fresh CRA build (env vars + GSC meta).
// All event firing is consent-gated. Scripts are NOT loaded until the user
// has accepted analytics (GA4) or advertising (Meta + TikTok) consent.
//
// Public API:
//   initAnalytics(consent)        — call on app boot & whenever consent changes
//   trackPageView(path, title?)   — fire on every SPA route change
//   track(eventName, params?)     — fire a custom conversion event
//   trackPurchase({...})          — purchase event, with built-in dedup
//
// Tracking IDs come ONLY from environment variables. Empty/placeholder values
// mean "no-op silently" — useful in preview / dev / before IDs are pasted.

const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const TIKTOK_PIXEL_ID = process.env.REACT_APP_TIKTOK_PIXEL_ID || "";

const isValidId = (v) =>
    !!v && typeof v === "string" && v.trim().length > 0 && !v.startsWith("[PASTE");

const HAS_GA4 = isValidId(GA4_ID);
const HAS_META = isValidId(META_PIXEL_ID);
const HAS_TIKTOK = isValidId(TIKTOK_PIXEL_ID);

const loaded = { ga4: false, meta: false, tiktok: false };
const queued = []; // queue events fired before the relevant script finished loading

// ---------- GA4 (Consent Mode v2) ----------
function bootstrapGtag() {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
        window.gtag = function gtag() {
            window.dataLayer.push(arguments);
        };
    }
}

function setGtagConsentDefaults() {
    bootstrapGtag();
    // Consent Mode v2 — default everything to denied until the user accepts.
    window.gtag("consent", "default", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "denied",
        functionality_storage: "granted",
        security_storage: "granted",
        wait_for_update: 500,
    });
}

function updateGtagConsent(consent) {
    bootstrapGtag();
    window.gtag("consent", "update", {
        analytics_storage: consent.analytics ? "granted" : "denied",
        ad_storage: consent.advertising ? "granted" : "denied",
        ad_user_data: consent.advertising ? "granted" : "denied",
        ad_personalization: consent.advertising ? "granted" : "denied",
    });
}

function loadGA4Script() {
    if (loaded.ga4 || !HAS_GA4) return;
    bootstrapGtag();
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_ID)}`;
    s.setAttribute("data-analytics", "ga4");
    document.head.appendChild(s);
    window.gtag("js", new Date());
    // send_page_view false — we send page_view manually on route changes.
    window.gtag("config", GA4_ID, { send_page_view: false, anonymize_ip: true });
    loaded.ga4 = true;
    flushQueueFor("ga4");
}

// ---------- Meta Pixel ----------
function loadMetaPixel() {
    if (loaded.meta || !HAS_META) return;
    if (typeof window === "undefined") return;
     
    !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.setAttribute("data-analytics", "meta");
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
     
    window.fbq("init", META_PIXEL_ID);
    loaded.meta = true;
    flushQueueFor("meta");
}

// ---------- TikTok Pixel ----------
function loadTikTokPixel() {
    if (loaded.tiktok || !HAS_TIKTOK) return;
    if (typeof window === "undefined") return;
     
    !(function (w, d, t) {
        w.TiktokAnalyticsObject = t;
        var ttq = (w[t] = w[t] || []);
        ttq.methods = [
            "page", "track", "identify", "instances", "debug", "on", "off", "once",
            "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent",
            "revokeConsent", "grantConsent",
        ];
        ttq.setAndDefer = function (t, e) {
            t[e] = function () {
                t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            };
        };
        for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
        ttq.instance = function (t) {
            for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
            return e;
        };
        ttq.load = function (e, n) {
            var r = "https://analytics.tiktok.com/i18n/pixel/events.js";
            var o = n && n.partner;
            ttq._i = ttq._i || {};
            ttq._i[e] = [];
            ttq._i[e]._u = r;
            ttq._t = ttq._t || {};
            ttq._t[e] = +new Date();
            ttq._o = ttq._o || {};
            ttq._o[e] = n || {};
            var s = document.createElement("script");
            s.type = "text/javascript";
            s.async = !0;
            s.setAttribute("data-analytics", "tiktok");
            s.src = r + "?sdkid=" + e + "&lib=" + t;
            var a = document.getElementsByTagName("script")[0];
            a.parentNode.insertBefore(s, a);
        };
        ttq.load(TIKTOK_PIXEL_ID);
        ttq.page();
    })(window, document, "ttq");
     
    loaded.tiktok = true;
    flushQueueFor("tiktok");
}

// ---------- Consent gating ----------
let currentConsent = { analytics: false, advertising: false };

export function initAnalytics(consent) {
    // Always bootstrap gtag stub + consent defaults so Consent Mode v2 works even
    // if the GA4 script never loads.
    if (HAS_GA4) {
        setGtagConsentDefaults();
    }
    applyConsent(consent || { analytics: false, advertising: false });
}

export function applyConsent(consent) {
    currentConsent = { analytics: !!consent.analytics, advertising: !!consent.advertising };
    if (HAS_GA4) {
        updateGtagConsent(currentConsent);
        if (currentConsent.analytics) loadGA4Script();
    }
    if (currentConsent.advertising) {
        if (HAS_META) loadMetaPixel();
        if (HAS_TIKTOK) loadTikTokPixel();
    } else {
        // Best-effort revoke for advertising trackers if they were previously loaded.
        if (loaded.meta && window.fbq) {
            try { window.fbq("consent", "revoke"); } catch (e) { /* tracking must never throw */ }
        }
        if (loaded.tiktok && window.ttq) {
            try { window.ttq.revokeConsent(); } catch (e) { /* tracking must never throw */ }
        }
    }
}

// ---------- Dispatch helpers ----------
function flushQueueFor(channel) {
    if (queued.length === 0) return;
    const next = [];
    for (const job of queued) {
        if (job.channel === channel) dispatchOnce(job);
        else next.push(job);
    }
    queued.length = 0;
    queued.push(...next);
}

function dispatchOnce(job) {
    try {
        job.fn();
    } catch (e) {
        // Tracking must never throw into the app.
         
        if (process.env.NODE_ENV !== "production") console.warn("analytics dispatch failed", e);
    }
}

function gaEvent(name, params) {
    if (!HAS_GA4 || !currentConsent.analytics) return;
    const fn = () => window.gtag("event", name, params || {});
    if (loaded.ga4 && window.gtag) fn();
    else queued.push({ channel: "ga4", fn });
}

function metaEvent(name, params, options) {
    if (!HAS_META || !currentConsent.advertising) return;
    const fn = () => {
        const isStandard = META_STANDARD.has(name);
        if (isStandard) window.fbq("track", name, params || {}, options || undefined);
        else window.fbq("trackCustom", name, params || {}, options || undefined);
    };
    if (loaded.meta && window.fbq) fn();
    else queued.push({ channel: "meta", fn });
}

function tiktokEvent(name, params) {
    if (!HAS_TIKTOK || !currentConsent.advertising) return;
    const fn = () => window.ttq.track(name, params || {});
    if (loaded.tiktok && window.ttq) fn();
    else queued.push({ channel: "tiktok", fn });
}

// Meta "standard" events — anything else gets sent via trackCustom.
const META_STANDARD = new Set([
    "PageView", "ViewContent", "Search", "AddToCart", "AddToWishlist",
    "InitiateCheckout", "AddPaymentInfo", "Purchase", "Lead", "CompleteRegistration",
    "Contact", "SubmitApplication", "Subscribe", "StartTrial", "Schedule",
]);

// ---------- Public event API ----------

// Sanitise free-text inputs before sending. Tracking events MUST NOT contain
// raw email addresses, passwords, encounter descriptions or other sensitive PII.
function sanitiseString(s, max = 80) {
    if (s == null) return "";
    const str = String(s);
    return str
        .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "")
        .slice(0, max)
        .trim();
}

export function trackPageView(path, title) {
    const cleanTitle = sanitiseString(title || "", 120);
    const params = { page_path: path, page_title: cleanTitle, page_location: window.location.href };
    gaEvent("page_view", params);
    if (HAS_META && currentConsent.advertising) {
        const fn = () => window.fbq("track", "PageView");
        if (loaded.meta && window.fbq) fn();
        else queued.push({ channel: "meta", fn });
    }
    if (HAS_TIKTOK && currentConsent.advertising) {
        const fn = () => window.ttq.page();
        if (loaded.tiktok && window.ttq) fn();
        else queued.push({ channel: "tiktok", fn });
    }
}

// Generic event dispatcher — sends to all 3 platforms with sensible mappings.
export function track(eventName, params) {
    const p = params || {};
    switch (eventName) {
        case "view_case":
            gaEvent("view_item", {
                item_id: sanitiseString(p.case_id, 60),
                item_category: sanitiseString(p.category || "", 60),
                content_type: "case",
            });
            metaEvent("ViewContent", {
                content_ids: [sanitiseString(p.case_id, 60)],
                content_type: "case",
                content_category: sanitiseString(p.category || "", 60),
            });
            tiktokEvent("ViewContent", {
                contents: [{ content_id: sanitiseString(p.case_id, 60), content_type: "case" }],
            });
            break;

        case "registration_started":
            gaEvent("sign_up_started", { method: p.method || "email" });
            metaEvent("Lead", { content_name: "registration_started" });
            tiktokEvent("ClickButton", { content_name: "registration_started" });
            break;

        case "registration_completed":
            gaEvent("sign_up", { method: p.method || "email" });
            metaEvent("CompleteRegistration", {
                status: true,
                content_name: "registration_completed",
            });
            tiktokEvent("CompleteRegistration", { content_name: "registration_completed" });
            break;

        case "login_completed":
            gaEvent("login", { method: p.method || "email" });
            metaEvent("login_completed", { method: p.method || "email" });
            tiktokEvent("login_completed", { method: p.method || "email" });
            break;

        case "pricing_view":
            gaEvent("view_promotion", { promotion_id: "pricing_page" });
            metaEvent("ViewContent", { content_name: "pricing", content_category: "billing" });
            tiktokEvent("ViewContent", {
                contents: [{ content_id: "pricing", content_type: "page" }],
            });
            break;

        case "checkout_started":
            gaEvent("begin_checkout", {
                value: Number(p.value || 0),
                currency: p.currency || "USD",
                items: [{
                    item_id: sanitiseString(p.plan_key, 60),
                    item_name: sanitiseString(p.tier, 60),
                    quantity: 1,
                    price: Number(p.value || 0),
                }],
            });
            metaEvent("InitiateCheckout", {
                value: Number(p.value || 0),
                currency: p.currency || "USD",
                content_ids: [sanitiseString(p.plan_key, 60)],
                content_type: "product",
                num_items: 1,
            });
            tiktokEvent("InitiateCheckout", {
                value: Number(p.value || 0),
                currency: p.currency || "USD",
                contents: [{
                    content_id: sanitiseString(p.plan_key, 60),
                    content_type: "product",
                    content_name: sanitiseString(p.tier, 60),
                    quantity: 1,
                    price: Number(p.value || 0),
                }],
            });
            break;

        case "annual_membership_selected":
            gaEvent("select_promotion", {
                promotion_id: "annual_membership",
                item_id: sanitiseString(p.tier, 60),
            });
            metaEvent("annual_membership_selected", { tier: sanitiseString(p.tier, 60) });
            tiktokEvent("annual_membership_selected", { tier: sanitiseString(p.tier, 60) });
            break;

        case "newsletter_signup":
            gaEvent("generate_lead", { method: "newsletter" });
            metaEvent("Subscribe", { content_name: "newsletter" });
            tiktokEvent("Subscribe", { content_name: "newsletter" });
            break;

        case "encounter_submission_started":
            gaEvent("encounter_submission_started", {});
            metaEvent("encounter_submission_started", {});
            tiktokEvent("encounter_submission_started", {});
            break;

        case "encounter_submission_completed":
            gaEvent("encounter_submission_completed", {
                encounter_id: sanitiseString(p.encounter_id, 60),
            });
            metaEvent("SubmitApplication", { content_name: "encounter_submission" });
            tiktokEvent("SubmitForm", { content_name: "encounter_submission" });
            break;

        case "contact_form_submitted":
            gaEvent("contact_form_submitted", {});
            metaEvent("Contact", { content_name: "contact_form" });
            tiktokEvent("Contact", { content_name: "contact_form" });
            break;

        case "cta_clicked":
            gaEvent("cta_click", {
                cta_id: sanitiseString(p.cta_id, 60),
                cta_label: sanitiseString(p.label, 60),
                cta_location: sanitiseString(p.location, 60),
            });
            metaEvent("cta_clicked", {
                cta_id: sanitiseString(p.cta_id, 60),
                cta_label: sanitiseString(p.label, 60),
            });
            tiktokEvent("ClickButton", {
                content_name: sanitiseString(p.cta_id || p.label, 60),
            });
            break;

        default:
            // Forward unrecognised event names as custom events to all platforms.
            gaEvent(eventName, p);
            metaEvent(eventName, p);
            tiktokEvent(eventName, p);
    }
}

// ---------- Purchase (with dedup on Stripe session/transaction ID) ----------
const PURCHASE_DEDUP_KEY = "unveiled_purchase_fired";

function loadDedupSet() {
    try {
        const raw = localStorage.getItem(PURCHASE_DEDUP_KEY);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return new Set(Array.isArray(arr) ? arr.slice(-200) : []);
    } catch { return new Set(); }
}

function saveDedupSet(set) {
    try {
        const arr = Array.from(set).slice(-200);
        localStorage.setItem(PURCHASE_DEDUP_KEY, JSON.stringify(arr));
    } catch (e) { /* localStorage unavailable — ignore */ }
}

export function trackPurchase({ transactionId, value, currency, tier, planKey, interval }) {
    const txId = sanitiseString(transactionId, 200);
    if (!txId) return false;
    const fired = loadDedupSet();
    if (fired.has(txId)) return false;
    fired.add(txId);
    saveDedupSet(fired);

    const cur = currency || "USD";
    const amt = Number(value || 0);
    const planId = sanitiseString(planKey, 60);
    const tierId = sanitiseString(tier, 60);

    gaEvent("purchase", {
        transaction_id: txId,
        value: amt,
        currency: cur,
        items: [{
            item_id: planId,
            item_name: tierId,
            quantity: 1,
            price: amt,
            item_variant: interval || "",
        }],
    });
    metaEvent("Purchase", {
        value: amt,
        currency: cur,
        content_ids: [planId],
        content_type: "product",
        num_items: 1,
    }, { eventID: txId });
    tiktokEvent("CompletePayment", {
        event_id: txId,
        value: amt,
        currency: cur,
        contents: [{
            content_id: planId,
            content_type: "product",
            content_name: tierId,
            quantity: 1,
            price: amt,
        }],
    });
    return true;
}

// ---------- Status helpers (used by debug panels / tests) ----------
export function analyticsStatus() {
    return {
        ga4: { configured: HAS_GA4, loaded: loaded.ga4 },
        meta: { configured: HAS_META, loaded: loaded.meta },
        tiktok: { configured: HAS_TIKTOK, loaded: loaded.tiktok },
        consent: currentConsent,
    };
}
